import {useCallback, useContext, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {fabric} from 'fabric';

import {selectStickers} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {RESET_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {
  selectInitEnvelopeStickers,
  selectHasStickersChanged,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {useCachedMedia} from '~/common/hooks/useCachedMedia';
import {EventCreateStickerContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Sticker/EventCreateStickerContext';
import {MixedStickerAssetType, StickerAssetType, StickerMediaType} from '~/common/types/assets';
import {StoredData, STICKER_UPLOAD_INDEXED_DB_STORE_NAME} from '~/common/indexed-db/dexieStores';

export type StickerMediasMapping = Record<string, string>;

export const useStickers = () => {
  const dispatch = useDispatch();
  const stickers = useSelector(selectStickers);
  const initStickers = useSelector(selectInitEnvelopeStickers);
  const hasStickersChanged = useSelector(selectHasStickersChanged);
  const {canvas} = useContext(EventCreateStickerContext);

  const {cachedMediaList: customStickers} = useCachedMedia(STICKER_UPLOAD_INDEXED_DB_STORE_NAME);

  const onStickersReset = useCallback(() => {
    if (canvas && initStickers?.objects) {
      canvas.loadFromJSON(
        {objects: initStickers.objects, version: fabric.version},
        () => {},
        () => {}
      );

      dispatch({type: RESET_DESIGN_OPTIONS, payload: ['envelopeStickers']});
    }
  }, [canvas, initStickers?.objects, dispatch]);

  const mapStickersMedias = useCallback(
    (stickerList: StickerAssetType[], mediaType: 'icon' | 'image'): MixedStickerAssetType[] =>
      stickerList.map(
        (item) =>
          ({
            id: item.id,
            url: item.media.find((m: StickerMediaType) => m.label === mediaType)?.url,
            cssCover: false,
          }) as MixedStickerAssetType
      ),
    []
  );
  const mapCustomStickersMedias = useCallback(
    (customStickersArray: StoredData[]): MixedStickerAssetType[] =>
      customStickersArray.map((item) => ({
        id: item.id,
        url: item.data || '',
        cssCover: true,
      })),
    []
  );

  const customStickerMedias = useMemo(
    () => mapCustomStickersMedias(customStickers),
    [mapCustomStickersMedias, customStickers]
  );
  const stickerMedias = useMemo(
    () => mapStickersMedias(stickers, 'image'),
    [mapStickersMedias, stickers]
  );
  const stickerIcons = useMemo(
    () => mapStickersMedias(stickers, 'icon'),
    [stickers, mapStickersMedias]
  );

  const customStickerMediasMapping = useMemo<StickerMediasMapping>(
    () => Object.fromEntries(customStickerMedias.map(({id, url}) => [id, url])),
    [customStickerMedias]
  );

  const stickerIconsMapping = useMemo(
    () => Object.fromEntries(stickerIcons.map(({id, url}) => [id, url])),
    [stickerIcons]
  );
  const stickerMediasMapping = useMemo<StickerMediasMapping>(
    () => Object.fromEntries(stickerMedias.map(({id, url}) => [id, url])),
    [stickerMedias]
  );
  return {
    stickerIcons,
    stickerMedias,
    stickerIconsMapping,
    stickerMediasMapping,
    customStickerMedias,
    customStickerMediasMapping,
    hasStickersChanged,
    onStickersReset,
  };
};
